<template>
  <div class="table-filter-container">
    <div
      v-for="(selectedFilter, index) in selectedFilters"
      id="table-filter"
      :key="getKey + index"
    >
      <el-select
        v-model="selectedFilters[index]"
        value-key="name"
        class="filter-type-select"
        :disabled="isInputDisabled"
        @change="delete selectedFilters[index].filterInput"
      >
        <el-option
          v-for="filter in config"
          :label="filter.label"
          :value="filter"
          v-show="!selectedFilters.map(sf => sf.name).includes(filter.name)"
        />
      </el-select>
      <el-date-picker
        v-if="selectedFilters[index].type === 'date'"
        v-model="selectedFilters[index].filterInput"
        class="filter-date"
        style="margin-right: 10px;"
        type="date"
        :clearable="false"
        format="yyyy. MM. dd."
        value-format="yyyy-MM-dd"
        :picker-options="{ firstDayOfWeek: 1 }"
        :disabled="isInputDisabled"
        @change="search"
      />
      <el-date-picker
        v-if="selectedFilters[index].type === 'dateRange'"
        v-model="selectedFilters[index].filterInput"
        class="filter-date"
        style="margin-right: 10px;"
        type="daterange"
        :clearable="false"
        format="yyyy. MM. dd."
        value-format="yyyy-MM-dd"
        :picker-options="dateRangeOptions"
        :disabled="isInputDisabled"
        @change="search"
      />
      <el-input
        v-if="selectedFilters[index].type === 'text'"
        v-model="selectedFilters[index].filterInput"
        style="margin-right: 10px"
        class="filter-input"
        :disabled="isInputDisabled"
        @keyup.native="debouncedSearch"
        @keyup.enter.native="search"
      >
        <el-button
          slot="append"
          icon="el-icon-search"
          :loading="buttonLoading"
          :disabled="isInputDisabled"
          @click="search"
        />
      </el-input>
      <el-select
        v-if="selectedFilters[index].type === 'select'"
        v-model="selectedFilter.filterInput"
        class="filter-option-select"
        :disabled="isInputDisabled"
        :value-key="!selectedFilters[index].valueKey ? 'id' : null"
        @change="search"
        filterable
      >
        <el-option
          v-for="option in selectedFilters[index].options"
          :value="selectedFilters[index].valueKey ? option[selectedFilters[index].valueKey] : option"
          :label="selectedFilters[index].labelCallback && selectedFilters[index].labelCallback(option)"
        />
      </el-select>
      <el-select
        v-model="selectedFilter.filterInput"
        v-if="selectedFilters[index].type === 'multipleSelect'"
        filterable
        class="filter-option-select"
        multiple
        collapse-tags
        @change="search"
      >
        <el-option
          v-for="option in selectedFilters[index].options"
          :value="selectedFilters[index].valueKey ? option[selectedFilters[index].valueKey] : option"
          :label="selectedFilters[index].labelCallback && selectedFilters[index].labelCallback(option)"
        />
      </el-select>
      <el-select
        v-model="selectedFilter.filterInput"
        v-if="selectedFilters[index].type === 'multipleRemoteSelect'"
        filterable
        remote
        class="filter-option-select"
        multiple
        :remote-method="data => remoteSearch(data, selectedFilter)"
        :loading="loading"
        collapse-tags
        @change="search"
      >
        <el-option
          v-for="option in remoteOptions"
          v-if="!selectedFilters[index].optionLabelCallback"
          :value="selectedFilters[index].valueKey ? option[selectedFilters[index].valueKey] : option"
          :label="selectedFilters[index].labelCallback && selectedFilters[index].labelCallback(option)"
        />
        <el-option
          v-for="option in remoteOptions"
          v-if="selectedFilters[index].optionLabelCallback"
          :value="selectedFilters[index].valueKey ? option[selectedFilters[index].valueKey] : option"
          :label="selectedFilters[index].labelCallback && selectedFilters[index].labelCallback(option)"
        >
          {{ selectedFilters[index].optionLabelCallback(option) }}
        </el-option>
      </el-select>
      <el-cascader
        v-if="selectedFilters[index].type === 'cascader'"
        v-model="selectedFilter.filterInput"
        :options="selectedFilters[index].options"
        :disabled="isInputDisabled"
        :props="{ checkStrictly: true, label: 'name', value: 'id', multiple: true, emitPath: false }"
        class="filter-option-select"
        collapse-tags
        @change="search"
      />
      <el-select
        v-model="selectedFilter.filterInput"
        v-if="selectedFilters[index].type === 'remote'"
        filterable
        remote
        class="filter-option-select"
        :remote-method="data => remoteSearch(data, selectedFilter)"
        :loading="loading"
        @change="search"
      >
        <el-option
          v-for="option in remoteOptions"
          :value="selectedFilters[index].valueKey ? option[selectedFilters[index].valueKey] : option"
          :label="selectedFilters[index].labelCallback && selectedFilters[index].labelCallback(option)"
        />
      </el-select>
      <el-button
        v-if="index === 0"
        icon="el-icon-plus"
        size="mini"
        @click="addFilter"
      >
        Új feltétel
      </el-button>
      <el-button
        size="mini"
        :loading="resetButtonLoading"
        :disabled="isResetDisabled"
        @click="reset"
        v-if="index === 0"
        type="info"
        plain
      >
        Alaphelyzet
      </el-button>
      <el-button
        v-if="index > 0"
        icon="el-icon-minus"
        circle
        @click="removeFilter(index)"
      />
    </div>
  </div>
</template>
<script>
import { debounce } from 'debounce';
import moment from 'moment';

export default {
  props: ['action', 'config'],
  data() {
    return {
      selectedFilters: [
        {...this.config.find(f => f.defaultSearch)}
      ],
      buttonLoading: false,
      resetButtonLoading: false,
      isResetDisabled: false,
      isInputDisabled: false,
      loading: false,
      remoteOptions: [],
      inputKey: 0,
      filterKey: this.getKey(),
      dateRangeOptions: {
        firstDayOfWeek: 1,
        shortcuts: [{
          text: 'Aktuális hét',
          onClick(picker) {
            const start = moment().weekday(1).toDate();
            const end = moment().weekday(7).toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző hét',
          onClick(picker) {
            const start = moment().subtract({ weeks: 1 }).weekday(1).toDate();
            const end = moment().subtract({ weeks: 1 }).weekday(7).toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Aktuális hónap',
          onClick(picker) {
            const start = moment().startOf('month').toDate();
            const end = moment().endOf('month').toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző hónap',
          onClick(picker) {
            const start = moment().subtract({ months: 1 }).startOf('month').toDate();
            const end = moment().subtract({ months: 1 }).endOf('month').toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző 3 hónap',
          onClick(picker) {
            const start = moment().subtract({ months: 3 }).startOf('month').toDate();
            const end = moment().subtract({ months: 1 }).endOf('month').toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Aktuális év',
          onClick(picker) {
            const start = moment().startOf('year').toDate();
            const end = moment().endOf('year').toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző év',
          onClick(picker) {
            const start = moment().subtract({ years: 1 }).startOf('year').toDate();
            const end = moment().subtract({ years: 1 }).endOf('year').toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'YTD',
          onClick(picker) {
            const start = moment().startOf('year').toDate();
            const end = moment().toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'MTD',
          onClick(picker) {
            const start = moment().startOf('month').toDate();
            const end = moment().toDate();
            picker.$emit('pick', [start, end]);
          }
        }]
      },
    };
  },

  mounted() {
    const query = Object.assign({}, this.$route.query);
    if (query && Object.entries(query).length) {
      this.selectedFilters = [];
      for (let [key, value] of Object.entries(query)) {
        const filter = this.config.find(c => c.name === key);
        if (filter.valueKey) {
          filter.filterInput = parseInt(value);
        } else {
          filter.filterInput = filter.options.find(v => v.id === parseInt(value));
        }
        if (filter) {
          this.selectedFilters.push(filter);
          this.filterKey = this.getKey();
        }
      }
    }
  },

  methods: {
    debouncedSearch: debounce(function (e) {
      this.search();
    }, 300),
    async search() {
      this.buttonLoading = true;
      this.isResetDisabled = true;
      let searchParams = {};
      this.selectedFilters.forEach(selectedFilter => {
        if (selectedFilter.nameCallback) {
          const index = selectedFilter.nameCallback(selectedFilter.filterInput);
          searchParams[index] = selectedFilter.filterInput.id;
        } else {
          searchParams[selectedFilter.name] = selectedFilter.filterInput;
        }
      });
      const queryString = new URLSearchParams(searchParams);
      await this.$store.dispatch(this.action, '?' + queryString.toString());
      this.buttonLoading = false;
      this.isResetDisabled = false;
      this.$router.replace(this.$route.path);
    },

    async reset() {
      this.resetButtonLoading = true;
      this.isInputDisabled = true;
      let defaultFilter = this.config.find(c => c.defaultSearch) ? {...this.config.find(c => c.defaultSearch)} : {};
      this.selectedFilters = [defaultFilter];
      const searchParams = {}
      if (defaultFilter.filterInput) {
        searchParams[defaultFilter.name] = defaultFilter.filterInput
      }
      const queryString = new URLSearchParams(searchParams)
      await this.$store.dispatch(this.action, '?' + queryString.toString());
      this.isInputDisabled = false;
      this.resetButtonLoading = false;
      this.$router.replace(this.$route.path);
    },

    async remoteSearch(query, selectedFilter) {
      this.loading = true;
      if (!query) {
        this.remoteOptions = [];
      } else {
        const queryString = new URLSearchParams({
          [selectedFilter.remoteSearchParam]: query,
        });
        this.remoteOptions = await this.$store.dispatch(selectedFilter.remoteAction, '?' + queryString.toString());
      }
      this.loading = false;
    },

    addFilter() {
      if (this.selectedFilters.length < this.config.length) {
        const filter = this.config.filter(f => !this.selectedFilters.map(sf => sf.name)
          .includes(f.name))[0];
        this.selectedFilters.push(filter);
      }
    },

    removeFilter(index) {
      delete this.selectedFilters[index].filterInput;
      this.selectedFilters.splice(index, 1);
      this.search();
    },

    getKey() {
      return Math.random()
      .toString(36)
      .substring(7);
    },
  }
};
</script>
<style lang="scss">
  #table-filter .filter-type-select .el-input .el-input__inner {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
    border-right: 0;
    width: 180px;
  }

  #table-filter .filter-type-select .el-input .el-input__inner:hover {
    border-color: #DCDFE6;
  }

  #table-filter .el-select .el-input.is-focus .el-input__inner {
    border-color: #DCDFE6;
  }

  #table-filter .el-select .el-input .el-input__inner:focus {
    border-color: #DCDFE6;
  }

  #table-filter .el-select:hover .el-input__inner {
    border-color: #DCDFE6;
  }

  #table-filter .filter-input {
    width: 246px;
  }

  #table-filter .filter-input.el-input .el-input__inner {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    width: 100%;
  }

  #table-filter .filter-input.el-input .el-input__inner:hover {
    border-color: #DCDFE6;
  }

  #table-filter .filter-input.el-input .el-input__inner:focus {
    border-color: #DCDFE6;
  }

  #table-filter .filter-date {
    width: 246px;
  }

  #table-filter .filter-date.el-input .el-input__inner {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    width: 100%;
  }

  #table-filter .filter-date.el-range-editor {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    width: 100%;
  }

  #table-filter .filter-date.el-input .el-input__inner:hover {
    border-color: #DCDFE6;
  }

  #table-filter .filter-date.el-input .el-input__inner:focus {
    border-color: #DCDFE6;
  }

  #table-filter .filter-option-select .el-input .el-input__inner {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  #table-filter .filter-option-select {
    width: 246px;
    margin-right: 10px;
  }

  .table-filter-container {
    margin: -20px -20px 30px -20px;
    padding: 20px;
    border-bottom: 1px solid #EBEEF5;

    #table-filter {
      display: flex;
      width: 660px;
      margin-bottom: 20px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  #table-filter .el-picker-panel [slot=sidebar], .el-picker-panel__sidebar {
    width: 150px;
  }
  #table-filter .el-picker-panel [slot=sidebar]+.el-picker-panel__body, .el-picker-panel__sidebar+.el-picker-panel__body {
    margin-left: 150px;
  }
</style>
